import {
  CalendarMonthOutlined,
  GroupOutlined,
  LinkOutlined,
  MoreVertRounded,
  PersonOutlineRounded,
  ThumbUp,
  ThumbUpOutlined,
  Tonality,
  VisibilityOffRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import { Box, IconButton, Link, Menu, MenuItem, Stack, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import {
  AvatarGroup,
  BorderlessTextField,
  ContextLabel,
  IssueDrawerContent,
  IssueStatusPicker,
  Table,
  TableDrawerButton,
  useDrawer,
} from "src/components";
import { DEFAULT_ISSUE_STATUS, IssuePrivacyType, Links } from "src/constants";
import { useSession } from "src/hooks";
import { IssueSummaryDto, useDeleteIssue, useEditIssue, useRemoveIssueUpvote, useUpvoteIssue } from "src/http";
import { useDestructiveConfirm } from "src/utilities";

const columnHelper = createColumnHelper<IssueSummaryDto>();

const columnsConfig = (
  handleIssueBlurred: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    issue: IssueSummaryDto,
  ) => void,
  handleShowDrawerClicked: (e: React.MouseEvent, issue: IssueSummaryDto) => void,
  handleUpvoteToggled: (issue: IssueSummaryDto) => void,
  handleActionsClicked: (e: React.MouseEvent<HTMLElement>, issue: IssueSummaryDto) => void,
  sessionUserId: string,
  theme: Theme,
) => {
  return [
    columnHelper.accessor("description", {
      id: "Name",
      enableHiding: false,
      size: 400,
      header: () => (
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="caption">NAME</Typography>
        </Box>
      ),
      cell: (props) => (
        <Stack
          direction="row"
          sx={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          <BorderlessTextField
            value={props.row.original.description}
            onClick={(e) => props.row.original.permissions.userCanEditDescription && e.stopPropagation()}
            onBlur={(e) => handleIssueBlurred(e, props.row.original)}
            placeholder="Enter item..."
            isEditable={props.row.original.permissions.userCanEditDescription}
            maxLength={512}
          />
          <TableDrawerButton onClick={(e) => handleShowDrawerClicked(e, props.row.original)} />
        </Stack>
      ),
    }),
    columnHelper.accessor("currentStatus", {
      id: "Status",
      size: 200,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <Tonality color="disabled" />
          <Typography variant="caption">STATUS</Typography>
        </Stack>
      ),
      cell: (props) => (
        <IssueStatusPicker
          variant="select"
          issueGuid={props.row.original.guid}
          status={props.row.original.currentStatus ?? DEFAULT_ISSUE_STATUS}
          hasDecision={props.row.original.hasDecision}
          disabled={!props.row.original.permissions.userCanEditStatus}
          teamSlug={props.row.original.assignedTeam?.slug}
          createdByUserGuid={props.row.original.createdByUser.userId}
        />
      ),
    }),
    columnHelper.accessor("upvotes", {
      id: "Upvotes",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <ThumbUpOutlined color="disabled" />
          <Typography variant="caption">UPVOTES</Typography>
        </Stack>
      ),
      cell: (props) => (
        <Tooltip
          title={
            props.row.original.upvotes.length > 0
              ? props.row.original.upvotes
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((u, i) => [
                    <Typography key={i} sx={{ display: "inline-block", fontSize: 13, marginRight: "2px" }}>
                      <Link href={Links.User(u.userId)}>{u.displayName}</Link>
                      {i < props.row.original.upvotes.length - 1 && ", "}
                    </Typography>,
                  ])
              : null
          }
        >
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <IconButton onClick={() => handleUpvoteToggled(props.row.original)}>
              {props.row.original.upvotes.some((x) => x.userId === sessionUserId) ? <ThumbUp /> : <ThumbUpOutlined />}
            </IconButton>
            <Typography>{props.row.original.upvotes.length} votes</Typography>
          </Stack>
        </Tooltip>
      ),
    }),
    columnHelper.accessor("context", {
      id: "Context",
      size: 300,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <LinkOutlined color="disabled" />
          <Typography variant="caption">CONTEXT</Typography>
        </Stack>
      ),
      cell: (props) => {
        if (!props.row.original.context) {
          return "";
        }

        return <ContextLabel context={props.row.original.context} hideTeamName />;
      },
    }),
    columnHelper.accessor("createdByUser", {
      id: "Creator",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <PersonOutlineRounded color="disabled" />
          <Typography variant="caption">CREATOR</Typography>
        </Stack>
      ),
      cell: (props) => (
        <AvatarGroup max={1} users={[props.row.original.createdByUser]} avatarProps={{ size: "xsmall" }} />
      ),
    }),
    columnHelper.accessor("collaborators", {
      id: "Collaborators",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <GroupOutlined color="disabled" />
          <Typography variant="caption">COLLABORATORS</Typography>
        </Stack>
      ),
      cell: (props) => (
        <AvatarGroup max={5} users={props.row.original.collaborators} avatarProps={{ size: "xsmall" }} />
      ),
    }),
    columnHelper.accessor("privacy", {
      id: "Privacy",
      size: 90,
      minSize: 90,
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <VisibilityRounded color="disabled" />
          <Typography variant="caption">PRIVACY</Typography>
        </Stack>
      ),
      cell: (props) => {
        const isPublic = props.row.original.privacy === IssuePrivacyType.Public;
        return (
          <Tooltip
            title={
              isPublic
                ? "Public to organization"
                : "Visible to creator, decision maker, collaborators, and assigned team members."
            }
          >
            <span>
              {isPublic ? <VisibilityRounded /> : <VisibilityOffRounded sx={{ color: theme.palette.error.main }} />}
            </span>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor("createdDateTimeUtc", {
      id: "Created On",
      header: () => (
        <Stack direction="row" sx={{ gap: 0.2, alignItems: "center" }}>
          <CalendarMonthOutlined color="disabled" />
          <Typography variant="caption">CREATED ON</Typography>
        </Stack>
      ),
      cell: (props) => (
        <Typography noWrap>
          {DateTime.fromISO(props.row.original.createdDateTimeUtc, { zone: "utc" })
            .toLocal()
            .toLocaleString(DateTime.DATE_MED)}
        </Typography>
      ),
    }),
    columnHelper.display({
      id: "Actions",
      size: 50,
      minSize: 50,
      enableResizing: false,
      enableHiding: false,
      meta: {
        hideColumnMenu: true,
        hideResizer: true,
      },
      cell: (props) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mx: 0.25 }}>
            <IconButton size="small" onClick={(e) => handleActionsClicked(e, props.row.original)}>
              <MoreVertRounded />
            </IconButton>
          </Box>
        );
      },
    }),
  ];
};

interface Props {
  tableId: string;
  issues: IssueSummaryDto[];
  enableActions?: boolean;
}

const IssuesTable = (props: Props) => {
  const sessionUser = useSession();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<IssueSummaryDto>();
  const { mutate: editIssue } = useEditIssue();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const { mutate: deleteIssue } = useDeleteIssue();
  const { showDrawer } = useDrawer();
  const theme = useTheme();
  const confirm = useDestructiveConfirm();

  const handleIssueBlurred = useMemo(
    () => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, issue: IssueSummaryDto) => {
      if (issue.permissions.userCanEditDescription) {
        editIssue({
          issueGuid: issue.guid,
          description: e.target.value,
          createdByUserId: issue.createdByUser.userId,
          teamSlug: issue.assignedTeam?.slug,
        });
      }
    },
    [editIssue],
  );

  const handleShowDrawerClicked = useMemo(
    () => (e: React.MouseEvent, issue: IssueSummaryDto) => {
      e.stopPropagation();

      showDrawer({
        content: <IssueDrawerContent issueGuid={issue.guid} />,
      });
    },
    [showDrawer],
  );

  const handleUpvoteToggled = useMemo(
    () => (issue: IssueSummaryDto) => {
      if (issue.upvotes.some((x) => x.userId === sessionUser.userId)) {
        removeIssueUpvote({
          issueGuid: issue.guid,
          teamSlug: issue.assignedTeam?.slug,
          createdByUserId: issue.createdByUser.userId,
          userId: sessionUser.userId,
        });
      } else {
        upvoteIssue({
          issueGuid: issue.guid,
          teamSlug: issue.assignedTeam?.slug,
          createdByUserId: issue.createdByUser.userId,
          user: {
            userId: sessionUser.userId,
            displayName: sessionUser.displayName,
            enabled: true,
            profilePhotoSmallUrl: sessionUser.profilePhotoSmallUrl,
          },
        });
      }
    },
    [sessionUser.userId, sessionUser.displayName, sessionUser.profilePhotoSmallUrl, removeIssueUpvote, upvoteIssue],
  );

  function handleActionsClicked(e: React.MouseEvent<HTMLElement>, issue: IssueSummaryDto) {
    setMenuAnchor(e.currentTarget);
    setSelectedItem(issue);
    e.stopPropagation();
  }

  async function handleDeleteClicked() {
    setMenuAnchor(null);

    if (!selectedItem || !selectedItem.permissions.userCanDeleteIssue) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Loop",
      content: "Are you sure you want to delete this loop?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteIssue({
      issueGuid: selectedItem.guid,
      createdByUserId: selectedItem.createdByUser.userId,
      teamSlug: selectedItem.assignedTeam?.slug,
    });
  }

  const columns = useMemo(
    () =>
      columnsConfig(
        handleIssueBlurred,
        handleShowDrawerClicked,
        handleUpvoteToggled,
        handleActionsClicked,
        sessionUser.userId,
        theme,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleIssueBlurred, handleShowDrawerClicked, handleUpvoteToggled, sessionUser.userId, theme, props.issues],
  );

  return (
    <>
      <Table
        tableId={props.tableId}
        data={props.issues}
        columns={columns}
        enableSorting={false}
        columnPinning={{
          right: ["Actions"],
        }}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={handleDeleteClicked}
          sx={{ color: theme.palette.error.main }}
          disabled={!selectedItem?.permissions.userCanDeleteIssue}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export { IssuesTable };
